import { api } from './client';
import type { CheckMode, EpicOwnerTeam, MergePolicy, Team, TeamLevel, TeamMember, TeamTreeNode, Status, PaginatedResponse } from './types';

export interface CreateTeamInput {
  name: string;
  key: string;
  level: TeamLevel;
  parentId?: string | null;
}

export interface UpdateTeamInput {
  id: string;
  name?: string;
}

export interface ReparentTeamInput {
  id: string;
  parentId: string | null;
  sortOrder?: number;
}

export interface CreateStatusInput {
  name: string;
  category: 'backlog' | 'unstarted' | 'started' | 'completed' | 'canceled';
  color: string;
  position?: number;
}

export interface UpdateStatusInput {
  name?: string;
  category?: 'backlog' | 'unstarted' | 'started' | 'completed' | 'canceled';
  color?: string;
  position?: number;
}

export const teamsApi = {
  list: () => api.get<PaginatedResponse<Team>>('/teams?limit=100'),

  tree: () => api.get<{ data: TeamTreeNode[] }>('/teams/tree'),

  epicOwners: () => api.get<{ data: EpicOwnerTeam[] }>('/teams/epic-owners'),

  get: (id: string) => api.get<{ data: Team }>(`/teams/${id}`),

  /** Connect, change or (with null) disconnect the project's repository. */
  setRepository: (id: string, url: string | null) =>
    api.put<{ data: { teamId: string; gitRemote: string | null } }>(`/teams/${id}/repository`, { url }),

  /** Set who merges the project's pull requests (ENG-343). Admins only. */
  setMergePolicy: (id: string, mergePolicy: MergePolicy) =>
    api.patch<{ data: Team }>(`/teams/${id}/merge-policy`, { mergePolicy }),
  /** ENG-350: the project's fast-mode default for runs (admin only). */
  setFastMode: (id: string, fastMode: boolean) =>
    api.patch<{ data: Team }>(`/teams/${id}/fast-mode`, { fastMode }),
  /** ENG-421: apply database migrations automatically when a change ships (admin only, ENG-419). */
  setAutoApplyMigrations: (id: string, autoApplyMigrations: boolean) =>
    api.patch<{ data: Team }>(`/teams/${id}/auto-apply-migrations`, { autoApplyMigrations }),
  setCheckMode: (id: string, checkMode: CheckMode) =>
    api.patch<{ data: Team }>(`/teams/${id}/check-mode`, { checkMode }),
  /** ENG-380: the project's minimum compliance score (50-100), or null for the default. Admin only. */
  setComplianceThreshold: (id: string, complianceThreshold: number | null) =>
    api.patch<{ data: Team }>(`/teams/${id}/compliance-threshold`, { complianceThreshold }),

  getChildren: (id: string) => api.get<{ data: Team[] }>(`/teams/${id}/children`),

  getDescendants: (id: string) => api.get<{ data: Team[] }>(`/teams/${id}/descendants`),

  getAncestors: (id: string) => api.get<{ data: Team[] }>(`/teams/${id}/ancestors`),

  create: (input: CreateTeamInput) => api.post<{ data: Team }>('/teams', input),

  update: ({ id, ...input }: UpdateTeamInput) =>
    api.put<{ data: Team }>(`/teams/${id}`, input),

  reparent: ({ id, ...input }: ReparentTeamInput) =>
    api.patch<{ data: Team }>(`/teams/${id}/reparent`, input),

  delete: (id: string) => api.delete<undefined>(`/teams/${id}`),

  // Team members
  getMembers: (teamId: string) => api.get<{ data: TeamMember[] }>(`/teams/${teamId}/members`),

  addMember: (teamId: string, userId: string) =>
    api.post<undefined>(`/teams/${teamId}/members`, { userId }),

  removeMember: (teamId: string, userId: string) =>
    api.delete<undefined>(`/teams/${teamId}/members/${userId}`),

  // Team statuses
  getStatuses: (teamId: string) =>
    api.get<{ data: Status[] }>(`/statuses?teamId=${encodeURIComponent(teamId)}`),

  createStatus: (teamId: string, input: CreateStatusInput) =>
    api.post<{ data: Status }>(`/statuses`, { ...input, teamId }),

  // teamId stays in the signature so callers can invalidate the team's status list.
  updateStatus: (_teamId: string, statusId: string, input: UpdateStatusInput) =>
    api.put<{ data: Status }>(`/statuses/${statusId}`, input),

  deleteStatus: (_teamId: string, statusId: string) =>
    api.delete<undefined>(`/statuses/${statusId}`),
};
